import React, { forwardRef, useState, useRef } from 'react';
import cn from 'classnames';
import { Placement } from '@popperjs/core';
import Popover from '@dealroadshow/uikit/core/components/Popover';
import Tooltip from '@dealroadshow/uikit/core/components/Tooltip';
import styles from './Select.scss';

interface ISelectOption {
  dateFrom: string,
  dateTo: string | null,
  disabled: boolean,
  label: string,
  value: string,
}

interface IProps {
  name: string,
  options?: ISelectOption[],
  selectedOption?: ISelectOption,
  onChangeSelectedOption?: (option: ISelectOption) => void,
  tooltipPlacement?: Placement,
  tooltipContent?: string,
  dataTest?: string,
  disabled?: boolean,
}

const Select = ({
  name,
  options,
  selectedOption,
  onChangeSelectedOption,
  tooltipPlacement,
  tooltipContent,
  dataTest,
  disabled,
}: IProps) => {
  const [isTooltipHidden, setIsTooltipHidden] = useState<boolean>(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const tooltipRef = useRef(null);

  const getDataTest = (label?) => {
    const dataLabel = label && typeof label === 'string' && label.toLowerCase().replace(/\W+/g, '_');
    return dataLabel ? `${ name }_${ dataLabel }` : name;
  };

  const hideTooltip = () => {
    if (tooltipRef.current?.isVisible) {
      tooltipRef.current.hideTooltip();
    }
  };

  const setDropDownOpen = () => {
    setIsTooltipHidden(true);
    setIsDropdownOpen(true);
    hideTooltip();
  };

  const setDropDownClose = () => {
    setIsTooltipHidden(false);
    setIsDropdownOpen(false);
  };

  const handleClickOption = (e) => {
    const isOptionSelected = e.target.innerText === selectedOption.label;
    const localSelectedOption: ISelectOption[] = options.filter((item) => item.label === e.target.innerText);

    if (!localSelectedOption[0].disabled && !isOptionSelected) {
      onChangeSelectedOption(localSelectedOption[0]);
      setDropDownClose();
    }

    e.stopPropagation();
  };

  const getDropDownMenu = () => (
    <div className={ styles.selectMenuWrapper }>
      <div className={ styles.selectMenu }>
        <span data-test={ getDataTest('options') }>
          {
            options.map((item: ISelectOption, index) => {
              const optionStyles = cn(styles.selectOption, {
                [styles.isSelected]: item.value === selectedOption.value,
                [styles.isDisabled]: item.disabled,
              });

              return (
                <div
                  data-test={ getDataTest('option') }
                  className={ optionStyles }
                      /* eslint-disable-next-line react/no-array-index-key */
                  key={ index }
                  onClick={ handleClickOption }
                >
                  { item.label }
                </div>
              );
            })
          }
        </span>
      </div>
    </div>
  );

  const PopoverforwardRef = forwardRef((props:{ dataTestAttr: string }, popoverRef) => (
    <div className={ cn(styles.selectControl, { [styles.isDisabled]: disabled }) }>
      <div
        className={ styles.selectPlaceholder }
        data-test={ props.dataTestAttr }
      >
        { selectedOption.label }
      </div>
      <span className={ styles.selectArrowZone }>
        { /* @ts-ignore */ }
        <span ref={ popoverRef } className={ styles.selectArrow } />
      </span>
    </div>
  ));

  return (
    <div
      className={ styles.finsightSelect }
      data-test={ dataTest }
    >
      <div className={ styles.selectWrapper }>
        <div className={ styles.select }>
          <Tooltip
            ref={ tooltipRef }
            className={ cn({
                  [styles.isHidden]: isTooltipHidden,
                }) }
            content={ tooltipContent }
            placement={ tooltipPlacement }
          >
            <Popover
              isFlipEnabled={ false }
              usePortal={ false }
              content={ getDropDownMenu() }
              isVisible={ isDropdownOpen }
              afterOpen={ setDropDownOpen }
              afterClose={ setDropDownClose }
              arrowClassName={ styles.arrowStyle }
              className={ styles.selectMenuContainer }
              contentClassName={ styles.selectMenuWrapper }
              disabled={ disabled }
            >
              <PopoverforwardRef dataTestAttr={ getDataTest('selected') } />
            </Popover>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default Select;
